import { useCallback, useMemo } from "react";
import useSWR, { useSWRConfig } from "swr";

import { deleteData, putData } from "@api/crud";
import { applicationEndpoints } from "@api/Endpoints";
import { AssociatedApplicationRoles } from "@users/applicationuser/models/ApplicationRole";

import { fetcher } from "@utils/swrUtils";

import { UserApplicationsResponse } from "../models/UserApplication";

export function useGetApplicationRoles(applicationId: string) {
  const url = applicationEndpoints.applicationRoles(applicationId);

  const { data, error, isLoading } = useSWR<AssociatedApplicationRoles, Error>(
    url,
    fetcher,
    { revalidateOnFocus: false }
  );

  return useMemo(
    () => ({
      applicationRoles: data?.applicationRoles ?? [],
      limit: data?.numberOfRoles ?? 0,
      allowUpdate: data?.updateUserAllowed ?? false,
      isLoading,
      error
    }),
    [data, isLoading, error]
  );
}

export function useInviteApplicationUser() {
  const { mutate } = useSWRConfig();

  const invite = useCallback(
    async (
      applicationId: string,
      userId: string,
      applicationRoles: string[]
    ) => {
      const url = applicationEndpoints.updateOrDeleteApplicationUsers(
        applicationId,
        userId
      );

      const result = await putData<{ applicationRoles: string[] }>(url, {
        applicationRoles: applicationRoles
      });
      await mutate(applicationEndpoints.applicationUsers(applicationId));

      await mutate(applicationEndpoints.userApplications(userId));
      return result;
    },
    [mutate]
  );

  return useMemo(() => ({ invite }), [invite]);
}

export function useUserApplications(userId: string) {
  const url = applicationEndpoints.userApplications(userId);
  const { data, error, isLoading } = useSWR<UserApplicationsResponse, Error>(
    url,
    fetcher,
    { revalidateOnFocus: false }
  );

  // If no `error` from useSWR() hook it means no significant server errors.
  // But it might be that data?.applications array contains objects like
  // {
  //     "data": null,
  //     "error": "Could not find association between application with id 'c2db386b-7db6-4e2b-883a-4674acb32c84' and user with id '7ccc24b5-d3eb-495c-a5c6-8e7c6ced6931'",
  //     "status": "NotFound"
  //   },
  return { userApplications: data?.applications, error, isLoading };
}

export function useRemoveUserApplication() {
  const { mutate } = useSWRConfig();
  const deleteApp = useCallback(
    async (applicationId: string, userId: string) => {
      const url = applicationEndpoints.updateOrDeleteApplicationUsers(
        applicationId,
        userId
      );
      const result = await deleteData(url);

      // This is to fix issue with cached data for userApplications after update/delete
      await mutate(applicationEndpoints.userApplications(userId));

      // And this is to refresh main list of users, although for page /organizations/users it seems to be not really relevant
      await mutate(applicationEndpoints.applicationUsers(applicationId));

      // Note
      // There is still bug witch "cached" values of previously selected Roles, and it is being used/set after Save

      return result;
    },
    [mutate]
  );

  return useMemo(() => ({ deleteApp }), [deleteApp]);
}
